import React, { useMemo, useRef } from "react";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { icon, label, number } from "../../../config";
import KendoEditor from "../../../editor/KendoEditor";
import { useCombinationKeys, usePopup } from "../../../helper/commonHooks";

/**
 * Description Popup component for workflow
 * @param {Object} props
 * @returns JSX
 * @ParentComponent WorkflowWrapper
 * @author Shivam Mishra
 */
const DescriptionPopup = ({
  desc,
  setDesc,
  showDescPopup,
  updateDescription,
  closeDescriptionPopup,
  acknowledgeMailEditor,
  headerLabel,
  taskKey,
  anchorElementId,
  formId,
  isFormSettings,
  formProjectId,
  workflowDescriptionProjectId,
  onGenerateAi,
  isGeneratingAi,
}) => {
  const [editable, setEditable] = React.useState(true);
  const [isDescUpdated, setDescUpdated] = React.useState(false);
  const [descUpdateCount, setDescUpdateCount] = React.useState(false);
  const viewRef = useRef(null);
  const editorRef = useRef(null);
  const editableRef = useRef(true);
  const { anchor } = usePopup();
  const ctrls = useCombinationKeys("s");

  React.useEffect(() => {
    if (anchorElementId && showDescPopup) {
      const anchorElement = document.getElementById(anchorElementId);
      if (anchorElement) {
        anchor.current = anchorElement;
      }
    }
  }, [anchorElementId, showDescPopup]);

  /**
   * Update the state of descUpdated and descUpdateCount based on the desc value
   * @returns None
   * @description
   * - If desc is truthy, set descUpdateCount to true and descUpdated to true if descUpdateCount is true.
   * - When the effect is cleaned up, reset descUpdated and descUpdateCount to false.
   * @author Shivam
   */
  React.useEffect(() => {
    if (desc) {
      setDescUpdateCount(true);
      if (descUpdateCount) {
        setDescUpdated(true);
      }
    }
    return () => {
      setDescUpdated(false);
      setDescUpdateCount(false);
    };
  }, [desc]);

  /**
   * Toggles edit mode when `ctrls` and `editable` are set.
   * This effect runs whenever `ctrls` changes.
   *@author Bhavana
   */
  React.useEffect(() => {
    ctrls && editable && toggleEditable(number.THREE);
  }, [ctrls]);

  /**
   * Toggle the editability of the editor and update description if editable
   * @returns None
   * @description
   * - If editable is true, update the description.
   * - Toggle the editable state and update related states.
   * - If editable is true, set descUpdated to false.
   * @author Shivam
   */
  const toggleEditable = async (id) => {
    if (editable && id === number.THREE) {
      updateDescription && updateDescription();
    }
    if (id === number.FOUR || id === number.TWO) {
      setEditable(!editable);
      editableRef.current = !editable;
    }
    setDescUpdated(false);
  };

  /**
   * Determine the appropriate icon for the editor based on conditions
   * @returns IconNumber
   * @description
   * - If the editor is editable and descUpdated is true, return 3.
   * - If the editor is editable, return 1.
   * - If the editor is not editable, return 2.
   * @author Shivam
   */
  const getEditorEditIcon = useMemo(() => {
    if (editableRef.current) {
      if (isDescUpdated) {
        return number.THREE;
      }
      return number.FOUR;
    }
    return number.TWO;
  }, [editableRef.current, isDescUpdated, desc]);

  return (
    <React.Fragment>
      <Popup
        show={showDescPopup}
        anchor={anchor.current}
        className='desc-popup-container workflow-desc-popup-container'
        popupClass={"desc-popup-child"}
        animate={false}>
        <div className='desc-popup-content' data-testid='descpopup-testid'>
          <div className='form-sub-heading d-flex p-2 justify-content-between align-items-center'>
            {headerLabel ?? label.DESCRIPTION}
            <div className='d-flex align-items-center gap-2'>
              {isFormSettings && (
                <button className='generate-ai-btn' onClick={onGenerateAi} title={isGeneratingAi ? "Generating..." : "Generate with AI"} disabled={isGeneratingAi}>
                  <span className='generate-ai-icon'>{icon.CIRCLE_STAR}</span>
                  <span className='generate-ai-text'>{isGeneratingAi ? "Generating..." : "Generate with AI"}</span>
                </button>
              )}
              <button className='desc-float-icon ml-3' onClick={closeDescriptionPopup} title={label.CLOSE_POPUP}>
                <Tooltip parentTitle={true} position='bottom'>
                  {icon.CLOSE}
                </Tooltip>
              </button>
            </div>
          </div>
          <div className='desc-popup-editor p-0 flex-grow-1'>
            <KendoEditor
              desc={desc}
              setDesc={setDesc}
              viewRef={viewRef}
              editableRef={editableRef}
              toggleEditable={toggleEditable}
              editorRef={editorRef}
              editorEditIcon={getEditorEditIcon}
              showEditIcons={true}
              acknowledgeMailEditor={acknowledgeMailEditor}
              taskKey={taskKey}
              formId={formId}
              isFormSettings={isFormSettings}
              formProjectId={formProjectId}
              workflowDescriptionProjectId={workflowDescriptionProjectId}
            />
          </div>
        </div>
      </Popup>
    </React.Fragment>
  );
};

export default DescriptionPopup;
